import React, { useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { HiOutlineTrendingUp } from "react-icons/hi";
import {
  CHART_FIXED_HEIGHT,
  CHART_WIDGET_HEIGHT,
  fillCostSeriesRange,
  formatAudSmart,
  formatCount,
  formatPeriodLabel,
  getChartPlotMargin,
  getChartYAxisProps,
  getCostAxisScale,
  getCostChartTickInterval,
} from "pages/ServiceProvider/SpSetting/AiCreditsUsage/aiUsageChartUtils";
import { ChartMetricStrip, ChartWidgetState } from "pages/ServiceProvider/SpSetting/AiCreditsUsage/ChartWidgetBody";
import { useChartAxisLayout } from "pages/ServiceProvider/SpSetting/AiCreditsUsage/useChartAxisLayout";
import { aiUsageTrendSubtitle } from "./aiUsageOverview.utils";

const CostTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;

  return (
    <div className="min-w-[180px] rounded-xl border border-gray-200 bg-white px-3.5 py-3 text-xs shadow-lg">
      <p className="font-semibold text-gray-900">{row.label}</p>
      <div className="mt-2 space-y-1.5 text-gray-600">
        <p>
          Credit spend:{" "}
          <span className="font-medium text-[#4B6CB7]">{formatAudSmart(row.cost_aud)}</span>
        </p>
        <p>
          AI calls:{" "}
          <span className="font-medium text-gray-900">{formatCount(row.call_count)}</span>
        </p>
        {row.shareOfTotal ? (
          <p>
            Share of period:{" "}
            <span className="font-medium text-gray-700">{row.shareOfTotal}</span>
          </p>
        ) : null}
      </div>
    </div>
  );
};

const PlatformAiCostOverTimeChart = ({
  series = [],
  from,
  to,
  loading = false,
  error = "",
  granularity = "day",
}) => {
  const isMonthlyView = granularity === "month";
  const isWeeklyView = granularity === "week";

  const chartData = useMemo(() => {
    if (!from || !to) {
      return [];
    }

    const filled = fillCostSeriesRange(series, from, to, granularity);
    const rows = filled.map((row) => ({
      ...row,
      cost_aud: Number(row.cost_aud) || 0,
      call_count: Number(row.call_count) || 0,
      label: formatPeriodLabel(row.period, granularity, {
        shortMonth: granularity === "month",
      }),
    }));
    const totalSpend = rows.reduce((sum, row) => sum + row.cost_aud, 0);

    return rows.map((row) => ({
      ...row,
      shareOfTotal:
        totalSpend > 0 ? `${((row.cost_aud / totalSpend) * 100).toFixed(1)}%` : "0%",
    }));
  }, [series, from, to, granularity]);

  const hasUsage = chartData.some((row) => row.cost_aud > 0 || row.call_count > 0);
  const xAxisTickInterval = getCostChartTickInterval(chartData.length, granularity);
  const { containerRef, compact } = useChartAxisLayout();
  const yAxisScale = useMemo(
    () => getCostAxisScale(chartData.map((row) => row.cost_aud), compact),
    [chartData, compact]
  );
  const summary = useMemo(() => {
    const totalSpend = chartData.reduce((sum, row) => sum + row.cost_aud, 0);
    const totalCalls = chartData.reduce((sum, row) => sum + row.call_count, 0);
    const peak = chartData.reduce(
      (best, row) => (row.cost_aud > (best?.cost_aud || 0) ? row : best),
      null
    );
    return { totalSpend, totalCalls, peak };
  }, [chartData]);

  const showDots = chartData.length <= 14;
  const peakLabel =
    granularity === "day" ? "Peak day" : granularity === "week" ? "Peak week" : "Peak month";

  return (
    <div
      className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
      style={{ height: CHART_WIDGET_HEIGHT }}
    >
      <div className="border-b border-gray-100 px-5 pt-4 pb-3">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
            <HiOutlineTrendingUp className="h-5 w-5" />
          </span>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Credit consumption</h3>
            <p className="text-xs text-gray-500">
              {aiUsageTrendSubtitle(granularity)}
              {from && to ? ` · ${from} → ${to}` : ""}
            </p>
          </div>
        </div>
      </div>

      {!loading && !error && chartData.length > 0 ? (
        <ChartMetricStrip
          items={[
            { label: "Total spend", value: formatAudSmart(summary.totalSpend) },
            { label: "Total calls", value: formatCount(summary.totalCalls) },
            {
              label: peakLabel,
              value: hasUsage ? summary.peak?.label || "—" : "—",
            },
          ]}
        />
      ) : null}

      <div
        ref={containerRef}
        className="flex min-w-0 flex-1 flex-col px-4 pt-2 pb-4 sm:px-5"
      >
        <ChartWidgetState
          loading={loading}
          error={error}
          empty={!loading && !error && chartData.length === 0}
          emptyTitle="No credit usage in this period"
          emptyHint="Usage will appear here after AI features run."
          skeletonVariant="area"
        >
          <div className="min-w-0 w-full" style={{ height: CHART_FIXED_HEIGHT }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartData}
                margin={getChartPlotMargin({
                  angledXAxis: isMonthlyView || isWeeklyView,
                  extraRight: isMonthlyView ? 8 : 16,
                })}
              >
                <defs>
                  <linearGradient id="platformAiCostFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4B6CB7" stopOpacity={0.22} />
                    <stop offset="100%" stopColor="#4B6CB7" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{
                    fontSize: isMonthlyView || isWeeklyView ? 9 : 10,
                    fill: "#9ca3af",
                  }}
                  axisLine={false}
                  tickLine={false}
                  interval={xAxisTickInterval}
                  minTickGap={isMonthlyView ? 0 : 12}
                  angle={isMonthlyView ? -42 : isWeeklyView ? -24 : 0}
                  textAnchor={isMonthlyView || isWeeklyView ? "end" : "middle"}
                  height={isMonthlyView ? 56 : isWeeklyView ? 48 : 28}
                  dy={isMonthlyView || isWeeklyView ? 2 : 4}
                />
                <YAxis
                  {...getChartYAxisProps(yAxisScale, { compact })}
                />
                <Tooltip content={<CostTooltip />} />
                <Area
                  type="monotone"
                  dataKey="cost_aud"
                  stroke="#4B6CB7"
                  strokeWidth={2}
                  fill="url(#platformAiCostFill)"
                  dot={showDots ? { r: 2.5, fill: "#4B6CB7", strokeWidth: 0 } : false}
                  activeDot={{ r: 4, fill: "#293F6A", stroke: "#fff", strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </ChartWidgetState>
      </div>
    </div>
  );
};

export default PlatformAiCostOverTimeChart;
